"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  convertFrenchMark,
  FRENCH_SCALES,
  type FrenchScale,
  frenchBands,
  summariseFrench,
} from "@/lib/tool-kit/intl-grades/french-grades";

/** English strings. A translated content file overrides these through `ui`. */
export const FRENCH_GRADE_CONVERTER_STRINGS = {
  scaleLabel: "Conversion table to use",
  scales: {
    "sciences-po": "Sciences Po Strasbourg (global and module conversion)",
    ncsu: "NC State University study abroad table",
    ags: "American Graduate School in Paris table",
  },
  scaleHint:
    "France has no official conversion. These are three published tables, and they disagree. Use the one the evaluating university names, or compare them.",
  coursesHeading: "Your marks out of 20",
  courseLabel: "Course {n}",
  markLabel: "Mark /20 for course {n}",
  creditsLabel: "Credits for course {n} (ECTS or any weight)",
  addCourse: "Add a course",
  removeCourse: "Remove",
  removeCourseAria: "Remove course {n}",
  results: "Result",
  average: "Weighted average /20",
  gpa: "GPA (4.0 scale)",
  averageLetter: "Letter for the average",
  mention: "Mention for the average",
  mentions: {
    ajourne: "Ajourné (below 10 — fail)",
    passable: "Passable (10–11.99)",
    "assez-bien": "Assez bien (12–13.99)",
    bien: "Bien (14–15.99)",
    "tres-bien": "Très bien (16 and above)",
  },
  tableHeading: "Course by course",
  colCourse: "Course",
  colMark: "Mark /20",
  colLetter: "Letter",
  colPoints: "Grade points",
  colCredits: "Credits",
  invalidMarkRows: "Skipped (not a mark from 0 to 20 or no credits): {rows}",
  empty: "Enter at least one mark between 0 and 20, with credits above zero.",
  sourceNote:
    "Source: {source}. The receiving university decides which table, if any, it applies.",
  sources: {
    "sciences-po": "Sciences Po Strasbourg, Grading and Transcripts (2023)",
    ncsu: "NC State University Study Abroad, Europe Grade Equivalencies",
    ags: "American Graduate School in Paris, Grading Scale",
  },
  globalNote:
    "The letter for the average uses Sciences Po Strasbourg's global conversion, which has no D: an average below 10 is an F.",
  agsNote:
    "This table lists whole numbers only, so a mark such as 14.5 is read in the band of the whole number below (14).",
  tableBandsHeading: "The table in use",
  bandMark: "From {min}",
  copyResult: "Copy result",
  copied: "Copied!",
  summary: "French average {average}/20 = GPA {gpa} ({letter}), {mention}",
} as const;

type Strings = typeof FRENCH_GRADE_CONVERTER_STRINGS;
type Format = ReturnType<typeof useToolNumber>;

interface Row {
  id: number;
  mark: string;
  credits: string;
}

const INITIAL_ROWS: Row[] = [
  { id: 1, mark: "14", credits: "6" },
  { id: 2, mark: "12.5", credits: "6" },
  { id: 3, mark: "11", credits: "6" },
  { id: 4, mark: "9.5", credits: "3" },
];

function CourseRow({
  row,
  index,
  strings,
  fmt,
  onChange,
  onRemove,
}: {
  row: Row;
  index: number;
  strings: Strings;
  fmt: Format;
  onChange: (next: Row) => void;
  onRemove: () => void;
}) {
  const n = fmt(index + 1);
  return (
    <div className="grid items-end gap-3 sm:grid-cols-[1fr_1fr_auto]">
      <TextField
        id={`fgc-mark-${row.id}`}
        label={t(strings.markLabel, { n })}
        numeric
        onChange={(mark) => onChange({ ...row, mark })}
        value={row.mark}
      />
      <TextField
        id={`fgc-credits-${row.id}`}
        label={t(strings.creditsLabel, { n })}
        numeric
        onChange={(credits) => onChange({ ...row, credits })}
        value={row.credits}
      />
      <button
        aria-label={t(strings.removeCourseAria, { n })}
        className={BUTTON_SECONDARY}
        onClick={onRemove}
        type="button"
      >
        {strings.removeCourse}
      </button>
    </div>
  );
}

export default function FrenchGradeConverterWidget() {
  const strings = useToolUi(FRENCH_GRADE_CONVERTER_STRINGS);
  const fmt = useToolNumber();
  const [scale, setScale] = useState<FrenchScale>("sciences-po");
  const [rows, setRows] = useState<Row[]>(INITIAL_ROWS);
  const [nextId, setNextId] = useState(5);

  const parsed = rows.map((row) => ({
    row,
    mark: parseNumberInput(row.mark),
    weight: parseNumberInput(row.credits),
  }));
  const courses = parsed.flatMap(({ mark, weight }) =>
    mark === null || weight === null ? [] : [{ mark, weight }]
  );
  const summary = summariseFrench(courses, scale);
  const skipped = parsed.flatMap(({ mark, weight }, index) =>
    mark !== null &&
    weight !== null &&
    convertFrenchMark(mark, scale) &&
    weight > 0
      ? []
      : [fmt(index + 1)]
  );

  const tableRows = parsed.flatMap(({ mark, weight }, index) => {
    const band = mark === null ? null : convertFrenchMark(mark, scale);
    if (mark === null || weight === null || band === null || weight <= 0) {
      return [];
    }
    return [
      [
        t(strings.courseLabel, { n: fmt(index + 1) }),
        fmt(mark, { maximumFractionDigits: 2 }),
        band.letter,
        fmt(band.points, {
          minimumFractionDigits: 1,
          maximumFractionDigits: 2,
        }),
        fmt(weight, { maximumFractionDigits: 2 }),
      ],
    ];
  });

  const bands = frenchBands(scale, "mark");

  return (
    <div className="space-y-6">
      <div className={`${CARD} space-y-5`}>
        <SelectField
          hint={strings.scaleHint}
          id="fgc-scale"
          label={strings.scaleLabel}
          onChange={setScale}
          options={FRENCH_SCALES.map((value) => ({
            value,
            label: strings.scales[value],
          }))}
          value={scale}
        />
        <h3 className={WIDGET_H3}>{strings.coursesHeading}</h3>
        <div className="space-y-3">
          {rows.map((row, index) => (
            <CourseRow
              fmt={fmt}
              index={index}
              key={row.id}
              onChange={(next) =>
                setRows((current) =>
                  current.map((item) => (item.id === row.id ? next : item))
                )
              }
              onRemove={() =>
                setRows((current) =>
                  current.filter((item) => item.id !== row.id)
                )
              }
              row={row}
              strings={strings}
            />
          ))}
        </div>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => {
            setRows((current) => [
              ...current,
              { id: nextId, mark: "", credits: "6" },
            ]);
            setNextId((value) => value + 1);
          }}
          type="button"
        >
          {strings.addCourse}
        </button>
      </div>

      <section aria-live="polite" className={`${RESULT_CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {summary === null ? (
          <Notice>{strings.empty}</Notice>
        ) : (
          <>
            <StatGrid>
              <StatCard
                label={strings.average}
                value={fmt(summary.average, { maximumFractionDigits: 2 })}
              />
              <StatCard
                emphasis
                label={strings.gpa}
                value={fmt(summary.gpa, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              />
              <StatCard
                label={strings.averageLetter}
                value={summary.averageBand.letter}
              />
            </StatGrid>
            <p className="text-foreground text-sm">
              {strings.mention}: {strings.mentions[summary.mention]}
            </p>
            {skipped.length > 0 ? (
              <p className="text-muted-foreground text-sm">
                {t(strings.invalidMarkRows, { rows: skipped.join(", ") })}
              </p>
            ) : null}
            <DataTable
              caption={strings.tableHeading}
              head={[
                strings.colCourse,
                strings.colMark,
                strings.colLetter,
                strings.colPoints,
                strings.colCredits,
              ]}
              rows={tableRows}
            />
            <p className="text-muted-foreground text-sm">
              {t(strings.sourceNote, { source: strings.sources[scale] })}
              {scale === "ags" ? ` ${strings.agsNote}` : ""}
              {scale === "sciences-po" ? ` ${strings.globalNote}` : ""}
            </p>
            <DataTable
              caption={strings.tableBandsHeading}
              head={[strings.colMark, strings.colLetter, strings.colPoints]}
              rows={bands.map((band) => [
                t(strings.bandMark, { min: fmt(band.min) }),
                band.letter,
                fmt(band.points, {
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 2,
                }),
              ])}
            />
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copyResult}
              small
              text={t(strings.summary, {
                average: fmt(summary.average, { maximumFractionDigits: 2 }),
                gpa: fmt(summary.gpa, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }),
                letter: summary.averageBand.letter,
                mention: strings.mentions[summary.mention],
              })}
            />
          </>
        )}
      </section>
    </div>
  );
}
